export type PollHandle = { cancel(): void };

/**
 * Poll until `done`, or until cancelled. Every screen that waits on the
 * pipeline uses this so there is exactly one place that can leak an interval.
 * Errors are surfaced but do not stop the loop — a single 5xx mid-generation
 * should not abandon a commission the parent already paid for.
 */
export function poll<T>(opts: {
  fetch: () => Promise<T>;
  done: (value: T) => boolean;
  onValue: (value: T) => void;
  onError?: (err: unknown) => void;
  onDone?: (value: T) => void;
  intervalMs?: number;
  timeoutMs?: number;
  onTimeout?: () => void;
}): PollHandle {
  const interval = opts.intervalMs ?? 1000;
  const timeout = opts.timeoutMs ?? 10 * 60 * 1000;
  const startedAt = Date.now();
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const tick = async () => {
    if (cancelled) return;
    try {
      const value = await opts.fetch();
      if (cancelled) return;
      opts.onValue(value);
      if (opts.done(value)) {
        opts.onDone?.(value);
        return;
      }
    } catch (err) {
      if (cancelled) return;
      opts.onError?.(err);
    }
    if (Date.now() - startedAt > timeout) {
      opts.onTimeout?.();
      return;
    }
    timer = setTimeout(tick, interval);
  };

  void tick();

  return {
    cancel() {
      cancelled = true;
      if (timer) clearTimeout(timer);
    },
  };
}
